import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ChipPicker.module.css';

export interface ChipOption {
  id: number;
  label: string;
  hint?: string;
}

export interface ChipPickerProps {
  selected: number[];
  options: ChipOption[];
  onChange: (next: number[]) => void;
  placeholder?: string;
  emptyLabel?: string;
  loading?: boolean;
  onSearch?: (query: string) => void;
}

export function ChipPicker({
  selected,
  options,
  onChange,
  placeholder = 'Pesquisar…',
  emptyLabel = 'Nenhum resultado',
  loading,
  onSearch,
}: ChipPickerProps) {
  const { t } = useTranslation(['common', 'nav', 'board', 'articles', 'analytics', 'feeddrive', 'config', 'strategist']);
  void t;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    onSearch?.(query);
  }, [query, onSearch]);

  const selectedOptions = useMemo(() => {
    const map = new Map(options.map((o) => [o.id, o]));
    return selected.map((id) => map.get(id)).filter(Boolean) as ChipOption[];
  }, [selected, options]);

  const visibleOptions = useMemo(() => {
    const q = query.toLowerCase();
    return options
      .filter((o) => !selected.includes(o.id))
      .filter((o) => (q ? o.label.toLowerCase().includes(q) : true))
      .slice(0, 20);
  }, [options, selected, query]);

  const add = (id: number) => {
    onChange([...selected, id]);
    setQuery('');
    inputRef.current?.focus();
  };

  const remove = (id: number) => {
    onChange(selected.filter((s) => s !== id));
  };

  return (
    <div className={styles.root}>
      <div className={styles.chips}>
        {selectedOptions.map((opt) => (
          <span key={opt.id} className={styles.chip}>
            {opt.label}
            <button
              type="button"
              className={styles.chipRemove}
              onClick={() => remove(opt.id)}
              aria-label={`Remover ${opt.label}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={selectedOptions.length === 0 ? placeholder : ''}
          className={styles.input}
        />
      </div>

      {open && (
        <div className={styles.dropdown}>
          {loading && <p className={styles.empty}>A pesquisar…</p>}
          {!loading && visibleOptions.length === 0 && (
            <p className={styles.empty}>{emptyLabel}</p>
          )}
          {visibleOptions.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={styles.option}
              onClick={() => add(opt.id)}
            >
              <span>{opt.label}</span>
              {opt.hint !== undefined && <span className={styles.hint}>{opt.hint}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
